import React from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform, useSpring } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { HOME } from "@/constants/testIds";
import { MASTER_LOGO } from "@/data/divisions";

const Nav = () => {
  const { lang, setLang, t } = useLang();
  const loc = useLocation();
  const onContact = loc.pathname.startsWith("/contact");
  const onHome = loc.pathname === "/";

  const { scrollY } = useScroll();
  // On the landing hero the logo is enormous; as user scrolls the first
  // ~400px it shrinks to its "nav" size. Contact page always uses small.
  const scaleRaw = useTransform(scrollY, [0, 400], [1.75, 1]);
  const scale = useSpring(scaleRaw, {
    stiffness: 120,
    damping: 24,
    mass: 0.6,
  });
  const originX = useTransform(scrollY, [0, 400], [0, 0]);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 left-0 right-0 z-50 px-6 md:px-10 py-5 flex items-start justify-between pointer-events-none"
      style={{
        background:
          "linear-gradient(to bottom, rgba(5,5,5,0.85) 0%, rgba(5,5,5,0.55) 60%, transparent 100%)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
    >
      <Link
        to="/"
        data-testid={HOME.navHome}
        className="select-none flex items-center pointer-events-auto"
      >
        <motion.img
          src={MASTER_LOGO}
          alt="2TProd Studios"
          className="h-14 md:h-20 lg:h-24 w-auto object-contain"
          style={
            onHome
              ? { scale, originX, transformOrigin: "top left" }
              : undefined
          }
        />
      </Link>

      <div className="flex items-center gap-6 md:gap-10 pointer-events-auto pt-2">
        <Link
          to="/"
          className={`text-[0.7rem] tracking-hair uppercase text-white/80 hover:text-white transition-colors ${
            !onContact ? "opacity-100" : "opacity-60"
          }`}
        >
          {t.nav.home}
        </Link>
        <Link
          to="/contact"
          data-testid={HOME.navContact}
          className={`text-[0.7rem] tracking-hair uppercase text-white/80 hover:text-white transition-colors ${
            onContact ? "opacity-100" : "opacity-60"
          }`}
        >
          {t.nav.contact}
        </Link>
        <div className="flex items-center gap-1 border border-white/40 rounded-full px-2 py-1 bg-black/40">
          {["en", "fr"].map((code) => (
            <button
              key={code}
              data-testid={`${HOME.langToggle}-${code}`}
              onClick={() => setLang(code)}
              className={`text-[0.62rem] uppercase tracking-widest px-2 py-0.5 rounded-full transition-colors ${
                lang === code
                  ? "bg-white text-black"
                  : "text-white/70 hover:text-white"
              }`}
            >
              {code}
            </button>
          ))}
        </div>
      </div>
    </motion.header>
  );
};

export default Nav;
